import React from 'react';
import AlignedPhotoFrame from '../AlignedPhotoFrame';
import { CardShell, BrandFooter, PhotoTag, scaleFor, ORANGE, HAIRLINE } from '../CardBits';

// Template E — Quad Grid. Two before/after pairs at once.
// BEFORE fills the left column, AFTER the right, so each ROW is one pair.
// Wide (16:9) reflows to a single 4-across strip: at 1920x1080 a 2x2 of 9:16
// photos would be 303px wide each, while 4-across gives 480px.
//
// EXPORT SAFETY: no object-fit / clip-path / filter. See lib/photoRender.js.
export default function QuadGridCard({ model, W, H, form }) {
  const s = scaleFor(H);
  const solidBar = form === 'portrait';
  const barH = solidBar ? 300 * s : 0;
  const photoH = H - barH;
  const wide = form === 'wide';
  const gap = 4 * s;

  const pairs = model.pairs || [{ before: model.before, after: model.after, poseLabel: model.poseLabel }];
  const slots = pairs.flatMap((p) => [
    { side: p.before, tone: 'dark', tag: model.labels.before, poseLabel: p.poseLabel },
    { side: p.after, tone: 'accent', tag: model.labels.after, poseLabel: null },
  ]);

  const cellW = wide ? (W - gap * 3) / 4 : (W - gap) / 2;
  const cellH = wide ? photoH : (photoH - gap) / 2;

  // Wide lays the four out left-to-right in pair order (b1 a1 b2 a2); the
  // others stack the pairs as rows with BEFORE always on the left.
  const position = (i) => (wide
    ? { left: i * (cellW + gap), top: 0 }
    : { left: (i % 2) * (cellW + gap), top: Math.floor(i / 2) * (cellH + gap) });

  return (
    <CardShell W={W} H={H} background="#000000">
      <div style={{ position: 'relative', width: W, height: photoH }}>
        {slots.map((slot, i) => {
          const { left, top } = position(i);
          return (
            <div key={i} style={{ position: 'absolute', left, top, width: cellW, height: cellH }}>
              <AlignedPhotoFrame
                url={slot.side.url}
                backdropUrl={slot.side.backdropUrl}
                canonical={slot.side.canonical}
                manual={slot.side.manual}
                natural={slot.side.natural}
                slotW={cellW}
                slotH={cellH}
              />
              <PhotoTag tone={slot.tone} s={s} style={{ left: 16 * s, top: 16 * s }}>
                {slot.tag}
                {!solidBar && slot.side.weightLabel ? ` · ${slot.side.weightLabel}` : ''}
              </PhotoTag>
              {slot.poseLabel && (
                <PhotoTag tone="dark" s={s} style={{ left: 16 * s, bottom: 16 * s }}>
                  {slot.poseLabel}
                </PhotoTag>
              )}
            </div>
          );
        })}

        {!solidBar && (
          <>
            <div style={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: 240 * s,
              background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.92))',
            }} />
            <div style={{
              position: 'absolute', left: '50%', top: '50%',
              marginLeft: -170 * s, marginTop: -50 * s, width: 340 * s,
              background: ORANGE, borderRadius: 999, padding: `${14 * s}px 0`, textAlign: 'center',
            }}>
              <div style={{ fontSize: 56 * s, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {model.hero.value}{model.hero.unit}
              </div>
              {model.hero.sub && (
                <div style={{ fontSize: 22 * s, letterSpacing: 3 * s, opacity: 0.85 }}>{model.hero.sub}</div>
              )}
            </div>
            <div style={{ position: 'absolute', left: 30 * s, right: 30 * s, bottom: 26 * s }}>
              <BrandFooter handleLabel={model.handleLabel} s={s} bordered={false} />
            </div>
          </>
        )}
      </div>

      {solidBar && (
        <div style={{ height: barH, background: '#0a0a0a', padding: `${26 * s}px ${30 * s}px ${20 * s}px` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <SideBlock side={model.before} s={s} align="left" />
            <div style={{ textAlign: 'center', paddingBottom: 6 * s }}>
              {model.hero.sub && (
                <div style={{ fontSize: 22 * s, letterSpacing: 3 * s, opacity: 0.45 }}>
                  {model.hero.sub.toUpperCase()}
                </div>
              )}
              <div style={{ fontSize: 72 * s, fontWeight: 800, color: ORANGE, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {model.hero.value}
                <span style={{ fontSize: 30 * s }}>{model.hero.unit}</span>
              </div>
            </div>
            <SideBlock side={model.after} s={s} align="right" />
          </div>
          <div style={{ marginTop: 20 * s, borderTop: `${Math.max(1, 2 * s)}px solid ${HAIRLINE}`, paddingTop: 14 * s }}>
            <BrandFooter handleLabel={model.handleLabel} s={s} bordered={false} />
          </div>
        </div>
      )}
    </CardShell>
  );
}

function SideBlock({ side, s, align }) {
  return (
    <div style={{ textAlign: align }}>
      {side.dateLabel && (
        <div style={{ fontSize: 22 * s, letterSpacing: 3 * s, opacity: 0.45 }}>{side.dateLabel}</div>
      )}
      {side.weightLabel && (
        <div style={{ fontSize: 56 * s, fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {side.weightLabel}
        </div>
      )}
      {side.bodyFatLabel && (
        <div style={{ fontSize: 26 * s, opacity: 0.5 }}>{side.bodyFatLabel}</div>
      )}
    </div>
  );
}
